import React from 'react';
import { motion } from 'framer-motion';
import { Section } from '../ui/Section';
import { HomeIcon, WalletIcon, MapPinIcon, ZapIcon, ShieldCheckIcon, GavelIcon } from 'lucide-react';
export function TheProduct() {
  const benefits = [{
    icon: <HomeIcon className="w-6 h-6 text-orange-500" data-id="element-397" />,
    title: 'Own Instead of Rent',
    desc: "Finally stop paying your landlord's mortgage and start building something that's yours."
  }, {
    icon: <WalletIcon className="w-6 h-6 text-orange-500" data-id="element-398" />,
    title: 'Skip the 20% Down',
    desc: 'Get into your first home without needing to save up a massive pile of cash.'
  }, {
    icon: <MapPinIcon className="w-6 h-6 text-orange-500" data-id="element-399" />,
    title: 'Deals in Your Area',
    desc: 'Find super-cheap homes hiding right in your own neighborhood.'
  }, {
    icon: <ShieldCheckIcon className="w-6 h-6 text-orange-500" data-id="element-400" />,
    title: 'Analyze Deals Safely',
    desc: 'Check for any hidden debts or surprises before you bid, so there is zero risk to you.'
  }, {
    icon: <GavelIcon className="w-6 h-6 text-orange-500" data-id="element-401" />,
    title: 'The Maximum Bid Trick',
    desc: 'Set your top price the smart way so you win the home without ever paying too much.'
  }, {
    icon: <ZapIcon className="w-6 h-6 text-orange-500" data-id="element-402" />,
    title: 'Start Tonight',
    desc: 'No experience needed — read it today and start finding deals in minutes.'
  }];
  return <Section theme="onyx" className="border-b border-slate-200 relative overflow-hidden" data-id="element-403">
      {/* Subtle orange dot pattern */}
      <div className="absolute inset-0 opacity-[0.03] pointer-events-none" style={{
      backgroundImage: 'radial-gradient(#F97316 1px, transparent 1px)',
      backgroundSize: '32px 32px'
    }} data-id="element-404"></div>

      <div className="relative z-10" data-id="element-405">
        <div className="mb-6 flex flex-col items-center" data-id="element-406">
          <div className="flex items-center gap-4 mb-4" data-id="element-407">
            <div className="h-1 w-8 bg-orange-500 rounded-full" data-id="element-408"></div>
            <span className="text-orange-600 text-xs font-bold tracking-wider uppercase" data-id="element-409">
              The Digital Guide
            </span>
            <div className="h-1 w-8 bg-orange-500 rounded-full" data-id="element-410"></div>
          </div>
        </div>

        <h2 className="text-3xl md:text-5xl font-display font-bold mb-6 text-center text-slate-900 leading-tight" data-id="element-411">
          A simple, easy-to-read{' '}
          <span className="text-orange-500" data-id="element-412">blueprint</span>.
        </h2>

        <p className="text-lg md:text-xl text-slate-600 text-center max-w-2xl mx-auto mb-16 font-sans leading-relaxed" data-id="element-413">
          Made for people who have never bought a home before. It's an easy,
          step-by-step checklist to safely buy your first home at auction for up
          to 50% off — with complete peace of mind.
        </p>

        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-100px'
      }} transition={{
        duration: 0.9,
        ease: [0.21, 0.47, 0.32, 0.98]
      }} className="relative max-w-4xl mx-auto" data-id="element-414">
          {/* Product image with soft rounded frame */}
          <div className="bg-white p-2 md:p-3 rounded-2xl border border-slate-200 shadow-soft-lg" data-id="element-415">
            <div className="rounded-xl overflow-hidden border border-slate-100" data-id="element-416">
              <img src="https://cdn.magicpatterns.com/uploads/iY37ay4CXuPtUwUqzT1hjf/The-2026-Home-Auction-Secrets-Blueprint-for-Beginners.png" alt="The 2026 Home Auction Secrets Blueprint for Beginners" className="w-full h-auto block" data-id="element-417" />
            </div>
          </div>

          {/* Decorative corner accents */}
          <div className="absolute -top-3 -left-3 w-6 h-6 border-l-2 border-t-2 border-orange-400 rounded-tl-lg hidden md:block" data-id="element-418"></div>
          <div className="absolute -top-3 -right-3 w-6 h-6 border-r-2 border-t-2 border-orange-400 rounded-tr-lg hidden md:block" data-id="element-419"></div>
          <div className="absolute -bottom-3 -left-3 w-6 h-6 border-l-2 border-b-2 border-orange-400 rounded-bl-lg hidden md:block" data-id="element-420"></div>
          <div className="absolute -bottom-3 -right-3 w-6 h-6 border-r-2 border-b-2 border-orange-400 rounded-br-lg hidden md:block" data-id="element-421"></div>
        </motion.div>

        {/* Core benefits — what this really means for you */}
        <div className="mt-16" data-id="element-422">
          <p className="text-center text-orange-600 text-xs font-bold tracking-wider uppercase mb-8" data-id="element-423">
            What This Really Means For You
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 w-full max-w-3xl mx-auto" data-id="element-424">
            {benefits.map((benefit, idx) => <div key={idx} className="flex items-start gap-4 px-5 py-5 bg-white rounded-xl border border-slate-200 shadow-sm w-full hover:border-orange-200 hover:shadow-md transition-all duration-300" data-id="element-425">
                <div className="shrink-0 bg-orange-50 p-2.5 rounded-lg" data-id="element-426">
                  {benefit.icon}
                </div>
                <div data-id="element-427">
                  <h3 className="text-slate-900 font-display font-bold text-lg leading-tight mb-1" data-id="element-428">
                    {benefit.title}
                  </h3>
                  <p className="text-slate-600 text-sm leading-relaxed" data-id="element-429">
                    {benefit.desc}
                  </p>
                </div>
              </div>)}
          </div>
        </div>
      </div>
    </Section>;
}